import { ApiResponse, http } from '@config/api';

export interface MonitoringAdminData {
  id: string;
  farmer: string;
  farmer_email: string;
  specialist: string;
  monitoring_type: string;
  farmer_id?: string;
  request_id?: string;
  monitoring_id?: string;
  payment_status?: string;
  area?: string;
  unit?: string;
  latitude?: number;
  longitude?: number;
  crop_id?: string;
  location_name?: string;
  invoice_url?: string;
}

export interface InvoiceDetails {
  id: string;
  first_name: string;
  last_name: string;
  id_number: string;
  email: string;
  mobile_number: string;
  company_name: string | null;
  company_id_number: string | null;
  representative_first_name: string | null;
  representative_last_name: string | null;
  price?: number;
}

export interface CropData {
  id: string;
  name: string;
  crop_type?: string;
  image?: string;
}

export interface Phase {
  id: string;
  phases_name: string;
  phases_open: string;
  phases_closed: string;
  phases_ordering: string;
  crop_id: string;
  meta: any;
  created_at: string;
  updated_at: string;
}

export interface PhasesList {
  phases?: Phase[];
  active_phase_id?: string;
}

export interface ActivePhase {
  name: string;
  phases_id: string;
  monitoring_id: string;
  attachedLink?: AttachedLink[];
}

export interface AttachedLink {
  id: string;
  phases_id: string;
  farmer_id: string;
  request_id: string;
  report_link: string;
  created_at: string;
  updated_at: string;
  phase_name: string;
}

export interface PaginationMeta {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

/**
 * Fetches admin monitoring data from the API with pagination support
 * @param page - The page number to fetch (default: 1)
 * @param perPage - The number of items per page (default: 10)
 * @param search - Optional search term to filter results
 * @returns Promise with the monitoring data and pagination metadata
 */
export async function getAdminMonitoringData(
  page: number = 1, 
  perPage: number = 10,
  search?: string
): Promise<PaginatedResponse<MonitoringAdminData>> {
  let url = `admin-monitoring-data?page=${page}&per_page=${perPage}`;
  
  // Add search parameter if provided
  if (search && search.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }
  
  const response = await http.get<ApiResponse<PaginatedResponse<MonitoringAdminData>>>('monitoring', url);
  
  if (response.data.data) {
    return response.data.data;
  }
  
  // If the API doesn't return the expected structure, provide a default
  return {
    data: [],
    meta: {
      total: 0,
      page: page,
      per_page: perPage,
      total_pages: 0
    }
  };
}

/**
 * Fetches active phases for a monitoring record
 * @param phasesId - The ID of the phases
 * @returns Promise with the phase data
 */
export async function getActivePhases(phasesId: string): Promise<ActivePhase> {
  const response = await http.get<ApiResponse<ActivePhase>>('monitoring', `admin-get-active-phases?phases=${phasesId}`);
  return response.data.data || { name: '', phases_id: '', monitoring_id: '', attachedLink: [] };
}

/**
 * Uploads a report for a monitoring record
 * @param phasesId - The ID of the phase
 * @param report - The F100 link/report URL
 * @param farmerId - The ID of the farmer
 * @param requestId - The ID of the request
 * @returns Promise with the update result
 */
export async function uploadReport(phasesId: string, report: string, farmerId: string, requestId: string): Promise<any> {
  const payload = {
    phases_id: phasesId,
    report: report,
    farmer_id: farmerId,
    request_id: requestId
  };
  
  const response = await http.post<ApiResponse<any>>('monitoring', 'admin-upload-report', payload);
  return response.data;
}

/**
 * Uploads a F100 file for a monitoring record
 * @param phasesId - The ID of the phase
 * @param fileKey - The S3 key of the uploaded file
 * @param farmerId - The ID of the farmer
 * @param requestId - The ID of the request
 * @returns Promise with the update result
 */
export async function uploadF100File(phasesId: string, fileKey: string, farmerId: string, requestId: string): Promise<any> {
  // Extract just the key from the file data if it's a JSON string
  let key = fileKey;
  try {
    if (fileKey.startsWith('{')) {
      const fileData = JSON.parse(fileKey);
      key = fileData.key || fileKey;
    }
  } catch (e) {
    // If parsing fails, use the original fileKey
    console.error('Error parsing file key:', e);
  }
  
  // Use the same payload structure as uploadReport but with key instead of report
  const payload = {
    phases_id: phasesId,
    key: key, // Use key instead of report
    farmer_id: farmerId,
    request_id: requestId
  };
  
  const response = await http.post<ApiResponse<any>>('monitoring', 'admin-upload-report', payload);
  return response.data;
}

/**
 * Updates the F100 link for a specific monitoring record
 * @param monitoringId - The ID of the monitoring record
 * @param f100Link - The F100 link to set
 * @returns Promise with the update result
 */
export async function updateF100Link(monitoringId: string, f100Link: string): Promise<any> {
  const response = await http.put<ApiResponse<any>>('monitoring', `v1/admin-monitoring-data/${monitoringId}/f100`, {
    f100_link: f100Link
  });
  return response.data;
}

/**
 * Marks a request as paid
 * @param phasesId - The ID of the phases
 * @returns Promise with the update result
 */
export async function markRequestAsPaid(phasesId: string): Promise<any> {
  const response = await http.get<ApiResponse<any>>('monitoring', `admin-mark-request-as-payed?phases=${phasesId}`);
  return response.data;
}

/**
 * Fetches phases list for a monitoring record
 * @param phasesId - The ID of the phases
 * @returns Promise with the phases list
 */
export async function getPhasesList(phasesId: string): Promise<Phase[]> {
  const response = await http.get<ApiResponse<Phase[]>>('monitoring', `admin-phases-list?phases=${phasesId}`);
  return response.data.data || [];
}

/**
 * Updates the active phase for a monitoring record
 * @param phasesId - The ID of the selected phase
 * @param monitoringId - The ID of the monitoring record
 * @returns Promise with the update result
 */
export async function updateActivePhase(phasesId: string, monitoringId: string): Promise<any> {
  const response = await http.post<ApiResponse<any>>('monitoring', `admin-open-closed-phases?phases=${phasesId}&id=${monitoringId}`);
  return response.data;
}

/**
 * Sends a notification that data has been uploaded for a phase
 * @param monitoringId - The ID of the monitoring record
 * @returns Promise with the notification result
 */
export async function sendDataUploadNotification(monitoringId: string): Promise<any> {
  const response = await http.get<ApiResponse<any>>('monitoring', `admin-data-upload-notification?monitoring=${monitoringId}`);
  return response.data;
}

/**
 * Fetches crop data by ID
 * @param cropId - The ID of the crop or array of crop IDs
 * @returns Promise with the crop data
 */
export async function getCropById(cropId: string | string[]): Promise<CropData | CropData[] | null> {
  try {
    // Handle single ID or array of IDs
    const ids = Array.isArray(cropId) ? cropId.join(',') : cropId;
    
    // Avoid API call if no valid IDs
    if (!ids || ids.length === 0) {
      return null;
    }
    
    const response = await http.get<ApiResponse<CropData[]>>('crops', `list/get-by-id/?ids=${ids}`);
    
    // Return appropriate result based on input type
    if (Array.isArray(cropId)) {
      return response.data.data || [];
    } else {
      return response.data.data && response.data.data.length > 0 ? response.data.data[0] : null;
    }
  } catch (error) {
    console.error('Error fetching crop data:', error);
    return null;
  }
}

/**
 * Fetches invoice details for a monitoring record
 * @param monitoringId - The ID of the monitoring record
 * @returns Promise with the invoice details
 */
export async function getInvoiceDetails(monitoringId: string): Promise<InvoiceDetails | null> {
  try {
    const response = await http.get<ApiResponse<InvoiceDetails>>('monitoring', `admin-invoice-details?phases=${monitoringId}`);
    return response.data.data || null;
  } catch (error) {
    console.error('Error fetching invoice details:', error);
    return null;
  }
}

/**
 * Updates invoice details for a monitoring record
 * @param monitoringId - The ID of the monitoring record
 * @param invoiceDetails - The updated invoice details
 * @returns Promise with the update result
 */
export async function updateInvoiceDetails(monitoringId: string, invoiceDetails: InvoiceDetails): Promise<boolean> {
  try {
    // Build query string with all parameters
    const queryParams = new URLSearchParams();
    queryParams.append('phases', monitoringId);
    
    // Add all invoice details to query parameters
    Object.entries(invoiceDetails).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    
    const queryString = queryParams.toString();
    
    // Make GET request with query parameters
    const response = await http.get<ApiResponse<any>>('monitoring', `admin-invoice-details?${queryString}`);
    return response.data.success || false;
  } catch (error) {
    console.error('Error updating invoice details:', error);
    return false;
  }
}

/**
 * Generates a PDF invoice for a monitoring record
 * @param monitoringId - The ID of the monitoring record
 * @param invoiceData - The invoice data
 * @returns Promise with the PDF URL
 */
export async function generateInvoicePdf(monitoringId: string, invoiceData: {
  invoice_id: string;
  first_name: string;
  last_name: string;
  id_number: string;
  price?: number;
}): Promise<string | null> {
  try {
    // Build query string with all parameters
    const queryParams = new URLSearchParams();
    queryParams.append('phases', monitoringId);
    queryParams.append('invoice_id', invoiceData.invoice_id);
    queryParams.append('first_name', invoiceData.first_name);
    queryParams.append('last_name', invoiceData.last_name);
    queryParams.append('id_number', invoiceData.id_number);
    
    // Add price if it exists
    if (invoiceData.price !== undefined) {
      queryParams.append('price', invoiceData.price.toString());
    }
    
    const queryString = queryParams.toString();
    
    // Make POST request with query parameters and empty body
    const response = await http.post<ApiResponse<string>>('monitoring', `admin-generate-pdf?${queryString}`, {});
    
    // The data field contains the URL directly as a string
    return response.data.data || null;
  } catch (error) {
    console.error('Error generating invoice PDF:', error);
    return null;
  }
}

/**
 * Submits an invoice and sends an email
 * @param monitoringId - The ID of the monitoring record
 * @param invoiceId - The ID of the invoice
 * @param pdfLink - The URL of the generated PDF
 * @param price - The price for the invoice
 * @returns Promise with the result
 */
export async function submitInvoiceAndSendEmail(monitoringId: string, invoiceId: string, pdfLink: string, price?: number): Promise<boolean> {
  try {
    // Build query string with all parameters
    const queryParams = new URLSearchParams();
    queryParams.append('phases', monitoringId);
    queryParams.append('invoice_id', invoiceId);
    queryParams.append('link', pdfLink);
    
    // Add price if it exists
    if (price !== undefined) {
      queryParams.append('price', price.toString());
    }
    
    const queryString = queryParams.toString();
    
    // Make GET request with query parameters
    const response = await http.get<ApiResponse<any>>('monitoring', `admin-submit-invoice-and-sent-email?${queryString}`);
    return response.data.success || false;
  } catch (error) {
    console.error('Error submitting invoice and sending email:', error);
    return false;
  }
} 